'use client';

import SectionTitle from '@components/SectionTitle';
import Grid from '@components/Grid';
import { useGetAdsQuery } from '@/src/api/api';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next-intl/link';
import React from 'react';
import LoadingSkeletonCategory from '@components/loadingSkeletons/loadingSkeletonCategory';
import Sorting from '@components/Sorting';
import Filter from '@components/Filter/Filter';
import { useRouter } from 'next/navigation';
import { useLocaleRoutes } from '@/src/utils/useLocaleRoutes';
import getOption from '@/src/utils/getOption';
import SignatureResult from '@components/SignatureResult';
import BannerFrontTopFixed from '@components/Banners/BannerFrontTopFixed';
import BannerFrontTopAuction from '@components/Banners/BannerFrontTopAuction';
import BannerFrontMiddleAuction from '@components/Banners/BannerFrontMiddleAuction';
import BannerFrontMiddle2Auction from '@components/Banners/BannerFrontMiddle2Auction';
import BannerFrontBottomAuction from '@components/Banners/BannerFrontBottomAuction';
import BannerFrontBottomFixed from '@components/Banners/BannerFrontBottomFixed';
import getDefaultSorting from '@/src/utils/getDefaultSorting';

export default function FrontPage({
  categoriesData,
}: {
  categoriesData?: any;
}) {
  const t = useTranslations('page.frontpage');
  const c = useTranslations('page.category');
  const { push } = useRouter();
  const path = useLocaleRoutes();
  const locale = useLocale();

  const { data: recentAds = { data: [] }, isFetching: isFetchingRecent } =
    useGetAdsQuery({
      filterParams: getOption('HAS_AUCTIONS')
        ? {
            auctions: 3,
          }
        : {},
      paginationParams: {
        sort: getDefaultSorting(),
        per_page: 15,
      },
    });

  const { data: soonEndingAds = { data: [] }, isFetching: isFetchingEnding } =
    useGetAdsQuery(
      {
        filterParams: getOption('HAS_AUCTIONS')
          ? {
              auctions: 3,
            }
          : {},
        paginationParams: {
          sort: 'time',
          per_page: 10,
        },
      },
      {
        skip: !getOption('HAS_AUCTIONS'),
      }
    );

  const { data: popularAds = { data: [] }, isFetching: isFetchingPopular } =
    useGetAdsQuery(
      {
        filterParams: getOption('HAS_AUCTIONS')
          ? {
              auctions: 3,
            }
          : {},
        paginationParams: {
          sort: '-popularity',
          per_page: 5,
        },
      },
      {
        skip: !getOption('HAS_AUCTIONS'),
      }
    );

  const redirectToListing = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const prefix =
      process.env.NEXT_PUBLIC_DEFAULT_LOCALE === locale ? '' : `/${locale}`;
    push(`${prefix}/${path('all')}?sort=${e.currentTarget.value}`);
  };

  return (
    <>
      <div className='mt-8' />
      {getOption('HAS_AUCTIONS') ? (
        <BannerFrontTopAuction />
      ) : (
        <BannerFrontTopFixed />
      )}
      <section className='mt-8'>
        <div className='mb-8 block flex flex-wrap justify-between gap-6 border-b-4 border-brand pb-2'>
          <h2 className='text-xl uppercase text-[#b2b2b2] md:text-3xl'>
            {t('latest')}
          </h2>
          <div className='relative top-[-5px] ml-auto grid w-full grid-cols-2 items-center gap-4 sm:gap-x-8 md:flex md:w-auto md:gap-4 lg:grid lg:grid-cols-3'>
            {getOption('HAS_AUCTIONS') ? (
              <Link
                href={`/${path('all')}?auctions=4,8`}
                className='col-span-2 font-bold text-[#ddd] underline hover:text-white focus:text-white lg:col-span-1'
              >
                {c('showConcluded')}
              </Link>
            ) : (
              <div className='col-span-1 hidden lg:block'></div>
            )}
            <Sorting
              sorting='-scope_price'
              onChange={(e) => redirectToListing(e)}
            />
            <Filter
              slug='all'
              redirect={true}
              categoriesData={categoriesData}
            />
          </div>
        </div>
        {isFetchingRecent && <LoadingSkeletonCategory />}
        {!isFetchingRecent && !!recentAds?.data?.length && (
          <>
            <Grid
              items={recentAds.data}
              rows={
                getOption('HAS_AUCTIONS')
                  ? 1
                  : soonEndingAds?.data?.length || popularAds?.data?.length
                    ? 2
                    : 3
              }
            />
            <div className='mt-10 flex justify-center'>
              <Link
                className='border border-brand bg-brand px-10 py-3 font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
                href={`/${path('all')}`}
              >
                {t('seeAll')}
              </Link>
            </div>
          </>
        )}

        {getOption('HAS_AUCTIONS') && (
          <>
            <div className='mt-12' />
            <BannerFrontMiddleAuction />
          </>
        )}
        {!getOption('HAS_AUCTIONS') && getOption('SIBLING_SITE_URL') && (
          <>
            <div className='mt-12' />
            <BannerFrontBottomFixed />
          </>
        )}

        {(isFetchingEnding || !!soonEndingAds?.data?.length) && (
          <>
            <div className='mt-12' />
            <SectionTitle title={t('soonEnding')} />
          </>
        )}
        {isFetchingEnding && <LoadingSkeletonCategory />}
        {!isFetchingRecent && !!soonEndingAds?.data?.length && (
          <Grid items={soonEndingAds.data} rows={2} />
        )}
      </section>
      {getOption('HAS_AUCTIONS') && (
        <>
          <div className='mt-8' />
          <BannerFrontMiddle2Auction />
        </>
      )}
      {(isFetchingPopular || !!popularAds?.data?.length) && (
        <>
          <div className='mt-8' />
          <SectionTitle title={t('popular')} />
        </>
      )}
      {isFetchingPopular && <LoadingSkeletonCategory />}
      {!isFetchingPopular && !!popularAds?.data?.length && (
        <Grid items={popularAds.data} rows={1} />
      )}

      {getOption('HAS_AUCTIONS') && (
        <>
          <div className='mt-8' />
          <BannerFrontBottomAuction />
        </>
      )}
      {/* SignatureResult is a modal to show document signature status.
      It's added here on the front page instead of Navbar, because
      it's only needed here as users get here by link */}
      <SignatureResult />
    </>
  );
}
